/**
 * Sharing a resource: who has access, adding someone, and the inheritance break.
 *
 * Two rules from the spec drive the whole layout, and both are stated on screen
 * rather than left to be discovered:
 *
 * 1. **Grants only add.** Adding one to a child can never take away access
 *    somebody inherited from a folder above. So there is no "remove inherited
 *    access" control here, because no such act exists.
 * 2. **Reducing access takes two deliberate steps.** Break inheritance — which
 *    copies the current grants down, so nobody loses anything at that moment —
 *    and then remove one. Each is separately audited, and the break is behind a
 *    confirmation because it is the one that is awkward to undo.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Capabilities, GrantDto, ResourceDto, SubjectDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { SubjectAvatar } from '../../components/SubjectAvatar';
import { SubjectPicker } from '../../components/SubjectPicker';
import { subjectKey } from '../../utils/subject';
import { useAsync } from '../../hooks/useAsync';
import { useSubjectSearch } from '../../hooks/useSubjectSearch';
import { useSubmit } from '../../hooks/useSubmit';
import { searchSubjects } from '../../api/identity';
import {
  breakInheritance,
  listGrants,
  restoreInheritance,
  share,
  unshare,
} from '../../api/resources';

/**
 * The four verbs, in the order they escalate.
 *
 * Keys, not labels — module-level data is evaluated once at import, so a
 * translated string here would freeze at whatever the language was on load.
 */
const VERBS = [
  { key: 'read', labelKey: 'share.verb.read', hintKey: 'share.verb.readHint' },
  { key: 'write', labelKey: 'share.verb.write', hintKey: 'share.verb.writeHint' },
  { key: 'delete', labelKey: 'share.verb.delete', hintKey: 'share.verb.deleteHint' },
  { key: 'share', labelKey: 'share.verb.share', hintKey: 'share.verb.shareHint' },
] as const satisfies ReadonlyArray<{
  key: keyof Capabilities;
  labelKey: string;
  hintKey: string;
}>;

export interface ShareDialogProps {
  resource: ResourceDto;
  open: boolean;
  onClose: () => void;
  /** Called when the resource itself changed — the inheritance flag lives on it. */
  onChanged?: () => void;
}

function GrantRow({
  grant,
  canEdit,
  onRemove,
}: {
  grant: GrantDto;
  canEdit: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation('files');

  /** "Read · Write", or what it says when none of them are held. */
  function verbSummary(verbs: Capabilities): string {
    const held = VERBS.filter((verb) => verbs[verb.key]).map((verb) => t(verb.labelKey));
    return held.length > 0 ? held.join(' · ') : t('share.noAccess');
  }

  return (
    <ListItem
      disableGutters
      secondaryAction={
        canEdit ? (
          <Tooltip title={t('share.removeGrant')}>
            <IconButton edge="end" onClick={onRemove} aria-label={t('share.removeGrantLabel')}>
              <DeleteOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
    >
      <ListItemAvatar>
        <SubjectAvatar type={grant.subjectType} />
      </ListItemAvatar>
      <ListItemText
        primary={grant.subjectLabel}
        secondary={verbSummary(grant.verbs)}
        slotProps={{ secondary: { variant: 'caption' } }}
      />
    </ListItem>
  );
}

export function ShareDialog({ resource, open, onClose, onChanged }: ShareDialogProps) {
  const { t } = useTranslation('files');
  const grants = useAsync(() => listGrants(resource.id), [resource.id], { enabled: open });

  const [subject, setSubject] = useState<SubjectDto | null>(null);
  const [verbs, setVerbs] = useState<Capabilities>({
    read: true,
    write: false,
    delete: false,
    share: false,
  });
  const [confirmBreak, setConfirmBreak] = useState(false);

  const search = useSubjectSearch(searchSubjects, { enabled: open });

  const mayShare = resource.capabilities.share;

  const add = useSubmit(async () => {
    if (!subject) return;
    await share(resource.id, { subjectType: subject.type, subjectId: subject.id, verbs });
    setSubject(null);
    search.reset();
    setVerbs({ read: true, write: false, delete: false, share: false });
    grants.reload();
  });

  const remove = useSubmit(async (grant: GrantDto) => {
    await unshare(resource.id, grant.subjectType, grant.subjectId);
    grants.reload();
  });

  const breakIt = useSubmit(async () => {
    const { copied } = await breakInheritance(resource.id);
    setConfirmBreak(false);
    grants.reload();
    onChanged?.();
    return copied;
  });

  const restore = useSubmit(async () => {
    await restoreInheritance(resource.id);
    grants.reload();
    onChanged?.();
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('share.title')}
          <Typography variant="body2" color="text.secondary" noWrap>
            {resource.name}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {mayShare ? (
              <Stack spacing={1.5}>
                <SubjectPicker
                  options={search.options}
                  value={subject}
                  loading={search.loading}
                  onChange={setSubject}
                  onQueryChange={search.setQuery}
                  label={t('share.addSubject')}
                />

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('share.whatTheyMayDo')}
                  </Typography>
                  <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {VERBS.map((verb) => (
                      <Tooltip key={verb.key} title={t(verb.hintKey)}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={verbs[verb.key]}
                              onChange={(event) =>
                                setVerbs((current) => ({
                                  ...current,
                                  [verb.key]: event.target.checked,
                                }))
                              }
                            />
                          }
                          label={t(verb.labelKey)}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>

                {add.error && <ErrorState error={add.error} severity="warning" />}

                <Box>
                  <Button
                    variant="contained"
                    disabled={!subject || add.pending}
                    onClick={() => void add.submit()}
                  >
                    {add.pending ? t('share.submitting') : t('share.submit')}
                  </Button>
                </Box>
              </Stack>
            ) : (
              <Alert severity="info">{t('share.readOnlyNotice')}</Alert>
            )}

            <Divider />

            <AsyncContent state={grants} placeholder="spinner">
              {({ grants: rows, inheritsGrants }) => (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>
                      {t('share.grantedHere')}
                    </Typography>
                    {inheritsGrants ? (
                      <Tooltip title={t('share.inheritingTooltip')}>
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                          <LinkIcon fontSize="small" color="success" />
                          <Typography variant="caption" color="text.secondary">
                            {t('share.inheriting')}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ) : (
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <LinkOffIcon fontSize="small" color="warning" />
                        <Typography variant="caption" color="text.secondary">
                          {t('share.notInheriting')}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {rows.length === 0 ? (
                    <EmptyState
                      dense
                      title={t('share.noGrantsTitle')}
                      description={
                        inheritsGrants
                          ? t('share.noGrantsInheriting')
                          : t('share.noGrantsIsolated')
                      }
                    />
                  ) : (
                    <List dense disablePadding>
                      {rows.map((grant) => (
                        <GrantRow
                          key={subjectKey({ type: grant.subjectType, id: grant.subjectId })}
                          grant={grant}
                          canEdit={mayShare}
                          onRemove={() => void remove.submit(grant)}
                        />
                      ))}
                    </List>
                  )}

                  {remove.error && <ErrorState error={remove.error} severity="warning" />}

                  {/* Inheritance is a folder-level idea; a file has no children
                      to pass anything down to. */}
                  {resource.kind === 'folder' && mayShare && (
                    <Box sx={{ pt: 1 }}>
                      {inheritsGrants ? (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<LinkOffIcon />}
                          onClick={() => setConfirmBreak(true)}
                        >
                          {t('share.stopInheriting')}
                        </Button>
                      ) : (
                        <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                          <Button
                            size="small"
                            startIcon={<LinkIcon />}
                            disabled={restore.pending}
                            onClick={() => void restore.submit()}
                          >
                            {t('share.resumeInheriting')}
                          </Button>
                          {restore.error && (
                            <ErrorState error={restore.error} severity="warning" />
                          )}
                        </Stack>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
            </AsyncContent>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>{t('share.close')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmBreak}
        title={t('share.breakDialog.title')}
        tone="primary"
        confirmLabel={t('share.breakDialog.confirm')}
        pending={breakIt.pending}
        error={breakIt.error}
        onClose={() => setConfirmBreak(false)}
        onConfirm={() => void breakIt.submit()}
        message={
          <Alert severity="info" icon={false}>
            <AlertTitle>{t('share.breakDialog.headline')}</AlertTitle>
            {t('share.breakDialog.body')}
          </Alert>
        }
      />
    </>
  );
}
